import { useEffect, useMemo, useState } from 'react';
import { ShieldCheck, Download, CheckCircle2, AlertTriangle } from 'lucide-react';
import { Button, Card, Pill } from '@nako/ui';
import { recomputeRegisterBalances } from '@nako/core';
import type { Business, ControlledRegisterEntry, PharmacyDrug } from '@nako/types';
import { isControlledSchedule } from '@nako/types';
import { supabase } from '@/lib/supabase';
import type { PharmacyData } from '@/pages/Pharmacy';

/**
 * Controlled-substances register — the statutory in/out ledger for S1–S4
 * drugs, one running balance per drug. Append-only: this view never edits.
 * The "verify" banner recomputes balances client-side and flags any row that
 * disagrees with its stored balance (tamper / missed-posting detection).
 */
export function ControlledRegister({ business, data }: { business: Business; data: PharmacyData }) {
  const controlledDrugs = useMemo(
    () => data.drugs.filter((d) => isControlledSchedule(d.schedule)),
    [data.drugs],
  );
  const [drugId, setDrugId] = useState<string>('');
  const [entries, setEntries] = useState<ControlledRegisterEntry[]>([]);
  const [loading, setLoading] = useState(false);

  const activeDrug: PharmacyDrug | undefined =
    controlledDrugs.find((d) => d.id === drugId) ?? controlledDrugs[0];

  useEffect(() => {
    if (!activeDrug) return;
    setLoading(true);
    void supabase
      .from('pharmacy_controlled_register')
      .select('*')
      .eq('business_id', business.id)
      .eq('drug_id', activeDrug.id)
      .order('created_at', { ascending: true })
      .then(({ data: rows }) => {
        setEntries((rows ?? []) as ControlledRegisterEntry[]);
        setLoading(false);
      });
  }, [activeDrug, business.id]);

  const check = useMemo(() => recomputeRegisterBalances(entries), [entries]);
  const discrepancySet = useMemo(() => new Set(check.discrepancies), [check.discrepancies]);

  function exportCsv() {
    if (!activeDrug) return;
    const header = [
      'Date',
      'Movement',
      'In',
      'Out',
      'Balance',
      'Patient',
      'Prescriber',
      'Reference',
    ];
    const lines = entries.map((e) =>
      [
        e.entry_date,
        e.movement,
        e.qty_in,
        e.qty_out,
        e.balance_after,
        e.patient_id ?? '',
        e.prescriber_name ?? '',
        (e.reference ?? '').replace(/,/g, ' '),
      ].join(','),
    );
    const csv = [header.join(','), ...lines].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `controlled-register-${activeDrug.name}-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    // Log the export as a PHI access event.
    void supabase.from('patient_audit_log').insert({
      business_id: business.id,
      action: 'export_records',
      context: { module: 'controlled_register', drug: activeDrug.name },
    });
  }

  if (controlledDrugs.length === 0) {
    return (
      <Card className="p-6 text-center">
        <ShieldCheck size={28} className="text-muted mx-auto mb-2 opacity-50" />
        <p className="text-muted text-sm">
          No controlled (S1–S4) drugs in your formulary. Mark a drug&rsquo;s schedule as S2–S4 to
          start a register.
        </p>
      </Card>
    );
  }

  return (
    <div>
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <select
          className="rounded-btn border-line h-10 border px-2 text-sm"
          value={activeDrug?.id ?? ''}
          onChange={(e) => setDrugId(e.target.value)}
        >
          {controlledDrugs.map((d) => (
            <option key={d.id} value={d.id}>
              {d.name} {d.strength ?? ''} ({d.schedule})
            </option>
          ))}
        </select>
        <Button variant="secondary" onClick={exportCsv} disabled={entries.length === 0}>
          <Download size={15} /> Export CSV
        </Button>
        <div className="ml-auto">
          {check.discrepancies.length === 0 ? (
            <Pill tone="green">
              <CheckCircle2 size={12} className="mr-1 inline" /> Balances verified
            </Pill>
          ) : (
            <Pill tone="red">
              <AlertTriangle size={12} className="mr-1 inline" /> {check.discrepancies.length}{' '}
              discrepancy
            </Pill>
          )}
        </div>
      </div>

      {loading ? (
        <p className="text-muted py-8 text-center text-sm">Loading…</p>
      ) : entries.length === 0 ? (
        <p className="text-muted py-8 text-center text-sm">
          No movements recorded for this drug yet.
        </p>
      ) : (
        <Card className="overflow-x-auto p-0">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-muted border-line border-b text-left text-xs">
                <th className="px-3 py-2">Date</th>
                <th className="px-3 py-2">Movement</th>
                <th className="px-3 py-2 text-right">In</th>
                <th className="px-3 py-2 text-right">Out</th>
                <th className="px-3 py-2 text-right">Balance</th>
                <th className="px-3 py-2">Ref</th>
              </tr>
            </thead>
            <tbody>
              {entries.map((e, i) => (
                <tr
                  key={e.id}
                  className={
                    'border-line border-b last:border-0 ' +
                    (discrepancySet.has(i) ? 'bg-[#FEE2E2]' : '')
                  }
                >
                  <td className="px-3 py-2">{e.entry_date}</td>
                  <td className="px-3 py-2 capitalize">{e.movement}</td>
                  <td className="px-3 py-2 text-right">{e.qty_in || ''}</td>
                  <td className="px-3 py-2 text-right">{e.qty_out || ''}</td>
                  <td className="px-3 py-2 text-right font-medium">
                    {e.balance_after}
                    {discrepancySet.has(i) && (
                      <span className="text-[#991B1B]"> → {check.expected[i]?.balance_after}</span>
                    )}
                  </td>
                  <td className="text-muted px-3 py-2">{e.reference ?? ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
    </div>
  );
}
